import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  IconBookLibrary,
  IconEcologyGlobeMessage1,
  IconRatingStar,
  useSiteContext
} from "@americanbible/uikit";
import { TabSelectDropdownMenu } from "../TabSelectDropdownMenu/TabSelectDropdownMenu";
import { TabSelectMenuListPane } from "../TabSelectDropdownMenu/TabSelectMenuListPane";
import { getMaxTabMenuHeight } from "../utils";
import { ListItem, BibleListItem } from "../ListItems";

export const AllBiblesList = ({
  bibles,
  currentBible,
  onBibleClick,
  siteBibleIds
}) => {
  const { title: siteTitle } = useSiteContext();

  const [activeMenuKey, setActiveMenuKey] = useState("bibles");
  const { id: currentBibleId } = currentBible;

  const [currentLanguageId, setCurrentLanguageId] = useState("site");
  const languages = Object.values(bibles).reduce((languages, { language }) => {
    if (!languages[language.id]) {
      languages[language.id] = {
        id: language.id,
        name: language.name,
        nameLocal: language.nameLocal
      };
    }
    return languages;
  }, {});

  const sortedLanguageArray = Object.keys(languages)
    .sort()
    .reduce((languageList, languageId) => {
      languageList.push(languages[languageId]);
      return languageList;
    }, []);

  const languageMenu = sortedLanguageArray.map(({ id, name, nameLocal }) => ({
    key: id,
    onSelect: () => {
      setCurrentLanguageId(id);
      setActiveMenuKey("bibles");
    },
    searchValues: [id, name, nameLocal],
    render: props => <ListItem {...props}>{nameLocal}</ListItem>
  }));

  languageMenu.unshift({
    key: "site",
    onSelect: () => {
      setCurrentLanguageId("site");
      setActiveMenuKey("bibles");
    },
    searchValues: [siteTitle],
    render: props => (
      <ListItem {...props}>
        <IconRatingStar size={22} style={{ marginRight: 8 }} /> {siteTitle}
      </ListItem>
    )
  });

  let bibleItems;
  if (currentLanguageId === "site") {
    bibleItems = siteBibleIds.map(id => bibles.find(bible => bible.id === id));
  } else {
    bibleItems = bibles
      .filter(
        bible => !currentLanguageId || bible.language.id === currentLanguageId
      )
      .sort((bibleA, bibleB) => bibleA.name.localeCompare(bibleB.name));
  }

  const bibleMenu = bibleItems.map(bible => ({
    key: bible.id,
    onSelect: () => onBibleClick(bible.id),
    searchValues: [bible.name, bible.abbreviationLocal],
    render: props => <BibleListItem {...props} bible={bible} />
  }));

  const maxListHeight = getMaxTabMenuHeight();
  return (
    <TabSelectDropdownMenu
      activeMenuKey={activeMenuKey}
      onTabClick={menuKey => setActiveMenuKey(menuKey)}
      menus={[
        {
          tab: <IconEcologyGlobeMessage1 />,
          tabPaneComponent: props => (
            <TabSelectMenuListPane {...props} maxListHeight={maxListHeight} />
          ),
          key: "languages",
          activeMenuItemIndex: languageMenu.findIndex(
            ({ key }) => key === currentLanguageId
          ),
          menuItems: languageMenu
        },
        {
          tab: <IconBookLibrary />,
          tabPaneComponent: props => (
            <TabSelectMenuListPane {...props} maxListHeight={maxListHeight} />
          ),
          key: "bibles",
          activeMenuItemIndex: bibleMenu.findIndex(
            ({ key }) => key === currentBibleId
          ),
          menuItems: bibleMenu
        }
      ]}
    />
  );
};

AllBiblesList.propTypes = {
  bibles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired
    })
  ),
  onBibleClick: PropTypes.func.isRequired,
  currentBible: PropTypes.shape({
    id: PropTypes.string
  }),
  siteBibleIds: PropTypes.arrayOf(PropTypes.string)
};
